const _excluded = ["id", "text"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import React, { useEffect, useState } from 'react';
import compact from 'lodash/compact';
import { Button, Callout, useOverlayContext, useStateInRef } from 'seagull';
import { useOpenMapServicesModal } from "../../integrations/QBO/MapServicesModal/useOpenMapServicesModal";
import { useOpenMapContactsModal } from "../../integrations/QBO/MapContactsModal/useOpenMapContactsModal";
import { qboIconMap } from "../../integrations/QBO/qboIconMap";
import { getQboSectionCallout } from "./FullPageInvoice.utils";
import { useStore, useStoreActions } from "../../contexts";
import { selectors } from "../../store/state-selectors";
import { Intent } from "../../infra/access-control";
import { handlePromise } from "../../infra/utils";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
export default function FullPageInvoiceQboSection(props) {
  const {
    invoice,
    integratedInvoice,
    contacts,
    serviceTemplates,
    relationship,
    reloadInvoice
  } = props;
  const {
    QBO_MAPPING_SYNC_FAILURE = false
  } = useFeatureFlags();
  const {
    showErrorToast,
    showSuccessToast
  } = useOverlayContext();
  const {
    api
  } = useStoreActions();
  const openMapServicesModal = useOpenMapServicesModal(compact(integratedInvoice.items.map(item => item.serviceTemplateId)), reloadInvoice);
  const openMapContactsModal = useOpenMapContactsModal(integratedInvoice.clientContactId, reloadInvoice);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSyncingPayment, setIsSyncingPayment] = useState(false);
  const isLinkedToQbo = useStore(selectors.integration.isLinkedToQbo);
  const getState = useStateInRef({
    isSyncing,
    isSyncingPayment,
    showSyncSuccessToast: formattedNumber => showSuccessToast("Invoice ".concat(formattedNumber, " was successfully synced"), 'sync-success-toast'),
    showSyncErrorToast: formattedNumber => showErrorToast("Failed to sync invoice ".concat(formattedNumber), 'sync-failure-toast'),
    showSyncPaymentSuccessToast: () => showSuccessToast('Payment was successfully synced', 'payment-sync-success'),
    showSyncPaymentFailureToast: () => showErrorToast('Failed to sync payment', 'payment-sync-failure')
  });
  useEffect(() => {
    if (!integratedInvoice.integration) return;
    const {
      isSyncing,
      isSyncingPayment,
      showSyncSuccessToast,
      showSyncErrorToast,
      showSyncPaymentFailureToast,
      showSyncPaymentSuccessToast
    } = getState();
    if (isSyncing) {
      setIsSyncing(false);
      if (integratedInvoice.integration.isInvoiceSyncFailed()) {
        showSyncErrorToast(integratedInvoice.formattedNumber);
      } else if (integratedInvoice.integration.isInvoiceRecorded() || integratedInvoice.integration.isPaymentRecorded()) {
        showSyncSuccessToast(integratedInvoice.formattedNumber);
      }
    }
    if (isSyncingPayment) {
      setIsSyncingPayment(false);
      if (integratedInvoice.integration.isPaymentSyncFailed()) {
        showSyncPaymentFailureToast();
      } else if (integratedInvoice.integration.isPaymentRecorded()) {
        showSyncPaymentSuccessToast();
      }
    }
  }, [integratedInvoice]);
  const callout = isLinkedToQbo ? getQboSectionCallout({
    QBO_MAPPING_SYNC_FAILURE,
    items: invoice.items,
    relationship,
    contacts,
    serviceTemplates,
    integration: integratedInvoice.integration,
    // TODO(ahuvi): no more need for integratedInvoice when merging QBO_MAPPING_SYNC_FAILURE
    invoiceStatus: invoice.status,
    syncAction: {
      id: 'sync-invoice',
      text: 'Sync',
      loading: isSyncing,
      loadingText: 'Syncing...',
      onClick: () => syncInvoice()
    },
    retrySyncAction: {
      id: 'retry-sync-invoice',
      text: 'Retry sync',
      loading: isSyncing,
      loadingText: 'Syncing...',
      onClick: () => syncInvoice({
        isRetry: true
      })
    },
    mapContactsAction: {
      id: 'map-contact',
      text: 'Map contact',
      onClick: openMapContactsModal
    },
    mapServicesAction: {
      id: 'map-services',
      text: 'Map services',
      intent: Intent.QboMapServices,
      onClick: openMapServicesModal
    },
    syncPaymentAction: {
      id: 'sync-payment',
      text: 'Sync',
      loading: isSyncingPayment,
      loadingText: 'Syncing...',
      onClick: syncPayment
    }
  }) : null;
  async function syncInvoice() {
    let {
      isRetry
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    setIsSyncing(true);
    const request = isRetry ? api.qbo.retrySyncInvoice : api.qbo.syncInvoice;
    const {
      success
    } = await handlePromise(request(invoice.id));
    if (!success) {
      getState().showSyncErrorToast(integratedInvoice.formattedNumber);
      setIsSyncing(false);
    }
  }
  async function syncPayment() {
    setIsSyncingPayment(true);
    const {
      success
    } = await handlePromise(api.qbo.syncPayment(invoice.id));
    if (!success) {
      getState().showSyncPaymentFailureToast();
      setIsSyncingPayment(false);
    }
  }
  return callout ? /*#__PURE__*/React.createElement(Callout, {
    intent: callout.intent,
    icon: qboIconMap[callout.status],
    "data-testid": "fpi-".concat(callout.id),
    withAction: !!callout.actions.length && /*#__PURE__*/React.createElement(React.Fragment, null, callout.actions.map(_ref => {
      let {
          id,
          text
        } = _ref,
        rest = _objectWithoutProperties(_ref, _excluded);
      return /*#__PURE__*/React.createElement(Button, _extends({
        key: id,
        "data-testid": id,
        skin: "primaryOutline"
      }, rest), text);
    }))
  }, callout.text) : null;
}